/**
 * Standard tab — flow validators
 * For Media Devoted | Created By Ciro Autiero
 *
 * Returns a list of human-readable validation issues. Empty list means the
 * draft is safe to Apply. We deliberately allow zero-weighted paths (Voluum
 * itself permits this; users use it to "pause" a path without disabling it).
 */

import type { VoluumFlowUpdate, VoluumPathUpdate } from '@trafficpulse/shared';

export type ValidationIssue = { path: string; message: string };

export function validateFlow(draft: VoluumFlowUpdate): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!draft.name || draft.name.trim().length === 0) {
    issues.push({ path: 'name', message: 'Flow name is required.' });
  }
  if (!Array.isArray(draft.defaultPaths) || draft.defaultPaths.length === 0) {
    issues.push({
      path: 'defaultPaths',
      message: 'At least one default path is required.',
    });
  }
  draft.defaultPaths?.forEach((p, idx) => {
    issues.push(...validatePath(p, `defaultPaths[${idx}]`));
  });
  draft.conditionalPathsGroups?.forEach((g, gIdx) => {
    g.paths?.forEach((p, pIdx) => {
      issues.push(
        ...validatePath(p, `conditionalPathsGroups[${gIdx}].paths[${pIdx}]`),
      );
    });
  });
  return issues;
}

function validatePath(path: VoluumPathUpdate, root: string): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (typeof path.weight !== 'number' || path.weight < 0) {
    issues.push({ path: `${root}.weight`, message: 'Weight must be ≥ 0.' });
  }
  const seenLanders = new Set<string>();
  path.landers?.forEach((l, idx) => {
    const id = l.lander?.id;
    if (!id) {
      issues.push({
        path: `${root}.landers[${idx}]`,
        message: 'Lander id missing.',
      });
      return;
    }
    if (seenLanders.has(id)) {
      issues.push({
        path: `${root}.landers[${idx}]`,
        message: `Duplicate lander ${id}.`,
      });
    }
    seenLanders.add(id);
  });
  const seenOffers = new Set<string>();
  path.offers?.forEach((o, idx) => {
    const id = o.offer?.id;
    if (!id) {
      issues.push({
        path: `${root}.offers[${idx}]`,
        message: 'Offer id missing.',
      });
      return;
    }
    if (seenOffers.has(id)) {
      issues.push({
        path: `${root}.offers[${idx}]`,
        message: `Duplicate offer ${id}.`,
      });
    }
    seenOffers.add(id);
  });
  return issues;
}
